import { AlertTriangle, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente.js';

/**
 * Aviso de que este nó não pode gravar.
 *
 * O ponto que o texto precisa deixar claro é que **o DNS continua no ar**. Um
 * banner genérico de erro faria o operador achar que os clientes ficaram sem
 * resolução e disparar um plano de contingência desnecessário — quando o que
 * aconteceu foi só o painel entrar em somente leitura.
 */

interface SaudeCluster {
  estado: 'OPERANDO' | 'SEM_QUORUM' | 'SINCRONIZANDO' | 'INDISPONIVEL';
  tamanho: number;
  aceitaEscrita: boolean;
  estadoLocal: string;
}

export function BannerQuorum() {
  const { data } = useQuery({
    queryKey: ['cluster', 'saude'],
    queryFn: () => api.get<SaudeCluster>('/cluster/saude'),
    // O estado do cluster muda em segundos; 15s é frequente o bastante para o
    // operador perceber sem transformar o painel numa fonte de tráfego.
    refetchInterval: 15_000,
    // Continua consultando com a aba em segundo plano: quem deixa o painel
    // aberto num telão de operação precisa ver a mudança sem interagir.
    refetchIntervalInBackground: true,
  });

  if (!data || data.aceitaEscrita) return null;

  const sincronizando = data.estado === 'SINCRONIZANDO';

  return (
    <div
      role="status"
      className={`flex items-start gap-3 border-b px-6 py-3 ${
        sincronizando
          ? 'border-aviso/20 bg-aviso-fundo text-aviso'
          : 'border-erro/20 bg-erro-fundo text-erro'
      }`}
    >
      {sincronizando ? (
        <Loader2 size={18} className="mt-0.5 shrink-0 animate-spin" />
      ) : (
        <AlertTriangle size={18} className="mt-0.5 shrink-0" />
      )}

      <div className="text-[13px] leading-relaxed">
        <p className="font-extrabold">
          {sincronizando
            ? 'Este nó está sincronizando com o cluster'
            : 'Este nó está em modo somente leitura'}
        </p>
        <p className="mt-0.5 opacity-90">
          {sincronizando ? (
            <>Aguarde a sincronização terminar para voltar a gravar alterações.</>
          ) : (
            <>
              O nó perdeu contato com a maioria do cluster e não pode gravar.{' '}
              <strong>O DNS continua respondendo normalmente</strong> — os clientes não são
              afetados. Verifique os outros nós ou acesse o painel por um deles.
            </>
          )}
        </p>
      </div>
    </div>
  );
}
